import { AreaData, Time } from "lightweight-charts";
import {
  DEFAULT_DURATION_LABELS,
  DEFAULT_UNAVAILABLE_RETURN_HEADING,
  durationReturnConfig,
  graphDurationBtns,
  SchemeReturns,
} from "./AMCPerformanceGraphConstants";

export type UnavailableReturnMessageOptions = {
  unavailableReturnHeading?: string;
  durationLabels?: Record<string, string>;
};

export const hasReturnValue = (value: number | string | null | undefined): boolean =>
  value !== null && value !== undefined && value !== "";

const DEFAULT_RETURN_CONFIG = durationReturnConfig["1Y"] ?? {
  field: "oneYrRet" as const,
  suffix: " annually for last 1 yr",
};

export const getReturnForDuration = (
  duration: string,
  returns: SchemeReturns = {}
): { value: number | string | null | undefined; suffix: string } => {
  const config = durationReturnConfig[duration] ?? DEFAULT_RETURN_CONFIG;

  return {
    value: returns[config.field],
    suffix: config.suffix,
  };
};

export const getLongestAvailableReturnDuration = (returns: SchemeReturns = {}): string | null => {
  for (let index = graphDurationBtns.length - 1; index >= 0; index -= 1) {
    const duration = graphDurationBtns[index];

    if (!duration) continue;

    const { value } = getReturnForDuration(duration, returns);

    if (hasReturnValue(value)) {
      return duration;
    }
  }

  return null;
};

export const getUnavailableReturnMessage = (
  selectedDuration: string,
  options: UnavailableReturnMessageOptions = {}
): string | null => {
  const durationLabels = {
    ...DEFAULT_DURATION_LABELS,
    ...options.durationLabels,
  };
  const durationLabel = durationLabels[selectedDuration];

  if (!durationLabel) {
    return null;
  }

  const template = options.unavailableReturnHeading?.trim() || DEFAULT_UNAVAILABLE_RETURN_HEADING;

  return template.replace("{durationLabel}", durationLabel);
};

export const convertChartData = (chartData: number[][] = []): AreaData<Time>[] => {
  if (!Array.isArray(chartData)) return [];

  return chartData
    .filter((item) => item.length >= 2)
    .map(([time, value]) => ({
      time: time as Time,
      value: Number(value),
    }))
    .sort((a, b) => (a.time as number) - (b.time as number));
};

export function getDateRange(duration: string) {
  const toDate = new Date();
  const fromDate = new Date(toDate);

  switch (duration) {
    case "1D":
      break;
    case "1W":
      fromDate.setDate(toDate.getDate() - 4);
      break;
    case "1M":
      fromDate.setMonth(toDate.getMonth() - 1);
      break;
    case "3M":
      fromDate.setMonth(toDate.getMonth() - 3);
      break;
    case "6M":
      fromDate.setMonth(toDate.getMonth() - 6);
      break;
    case "1Y":
      fromDate.setFullYear(toDate.getFullYear() - 1);
      break;
    default:
      throw new Error("Invalid duration");
  }

  return {
    fromDate: fromDate.toISOString().split("T")[0],
    toDate: toDate.toISOString().split("T")[0],
  };
}
